import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import I18n from 'i18n-js';
import { connect } from 'react-redux';
import {
  bulkUploadDeliveriesSelector,
  bulkUploadDeliveryIDsSelector,
  bulkUploadOfferConfigSelector,
} from 'selectors/new-bulk-upload-selectors';
import { Drawer, Form, Spin } from 'grab-ui';
import InputField from 'components/v2/input-field/input-field';
import {
  enablePaySDKWebSelector,
  enterpriseForPaySdkSelector,
  getUserCountryCodeFromState,
  isCashPaymentDisabledSelector,
  isCashlessPaymentDisabledSelector,
  selectedCitySelector,
  showEnterpriseTripInputSelector,
} from 'selectors/app-selector';
import { paymentMethodSelector } from 'selectors/booking-selectors';
import { deliveryFormFields } from 'views/v2/new-booking-page/delivery-form/common';
import PaymentMethodSelector from 'components/v2/anytime-booking/bulk-booking/payment-method-selector';
import PaySdkPaymentList from 'components/v2/pay-sdk/pay-sdk-payment-list';
import PaySdkPaymentRecommendation from 'components/v2/pay-sdk/pay-sdk-payment-recommendation';
import SelectField from 'components/v2/select-field/select-field';
import { loadPaySdkScript } from 'helper/pay-sdk-helper';
import { getDeliveryFinalDropOffs } from 'helper/anytime-booking';
import _sumBy from 'lodash/sumBy';
import _get from 'lodash/get';
import _every from 'lodash/every';
import { CASH_BY_RECIPIENT, CASH_BY_SENDER } from 'constants/booking-constants';
import { NewBookingDeliveryPropType, CityPropType } from 'common/prop-types';
import calcPrice from 'common/utils/calc-price';
import NewBookingDuck from 'ducks/new-booking-duck';
import { deliveryPriceReady } from 'common/anytime-booking/delivery';
import {
  CashPaymentMethods,
  generatePaymentMethodToRender,
  resolvePaySdkPaymentTypeID,
} from 'common/anytime-booking/payment-method';
import scribeNewBulkUploadDuck from 'ducks/scribe-new-bulk-upload-duck';
import RefreshFailedQuotesButton from '../common/RefreshFailedQuotesButton';

import { PayAndBookContext } from '../common/context';
import OfferSelector from './offer-selector';
import PriceSection from './price-section';
import styles from './pay-and-book.less';

const PayAndBook = ({
  dispatch,
  deliveryIDs,
  deliveries,
  offerConfig,
  form,
  showEnterpriseTripInput,
  enablePaySDKWeb,
  enterprise,
  isCashlessPaymentDisabled,
  isCashPaymentDisabled,
  paymentMethods,
  city,
  countryCode,
}) => {
  const payAndBookContext = useMemo(
    () => {
      const currency = _get(deliveries[0], 'quote.currency');
      return {
        currency,
        code: _get(currency, 'code'),
        totalDiscount: calcPrice(
          _sumBy(deliveries, delivery =>
            _get(delivery, 'quote.discount.discountAmount', 0),
          ),
          currency,
        ),
        totalOriginalFare: calcPrice(
          _sumBy(deliveries, delivery =>
            _get(delivery, 'quote.originalFare.lowerBound', 0),
          ),
          currency,
        ),
        totalFinalFare: calcPrice(
          _sumBy(deliveries, delivery =>
            _get(delivery, 'quote.finalFare.lowerBound', 0),
          ),
          currency,
        ),
        offerConfig,
      };
    },
    [deliveries, offerConfig],
  );

  useEffect(
    () => {
      // run once
      const { totalFinalFare, code } = payAndBookContext;
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeExpressBukUploadBookDefault(
          totalFinalFare,
          code,
          deliveries.length,
        ),
      );
    },
    [dispatch],
  );

  const tripCode = useMemo(() => _get(deliveries[0], 'detail.tripCode', ''), [
    deliveries,
  ]);

  const tripDescription = useMemo(
    () => _get(deliveries[0], 'detail.tripDescription', ''),
    [deliveries],
  );

  const refreshFailedQuotesButton = useMemo(
    () => {
      const isAllQuotesReady = _every(deliveries, deliveryPriceReady);
      if (isAllQuotesReady) {
        return null;
      }

      return (
        <div className={styles.refreshFailedQuotesButton}>
          <RefreshFailedQuotesButton deliveries={deliveries} />
        </div>
      );
    },
    [deliveries],
  );

  const serviceAllowCard = useMemo(
    () => _get(deliveries, '[0].config.paymentMethods', []).includes('CARD'),
    [deliveries],
  );

  const serviceAllowCash = useMemo(
    () =>
      _get(deliveries, '[0].config.paymentMethods', ['CASH']).includes('CASH'),
    [deliveries],
  );

  const isSameDayService = useMemo(
    () =>
      _get(deliveries, '[0].detail.configuration.serviceType') === 'SAME_DAY',
    [deliveries],
  );

  // CASH_BY_RECIPIENT is disabled for any delivery with multiple drop-offs.
  const isMSD = useMemo(
    () =>
      deliveries.some(delivery => {
        const dropOffs = getDeliveryFinalDropOffs(_get(delivery, 'detail'));
        return _get(dropOffs, 'length', 0) > 1;
      }),
    [deliveries],
  );

  const showPaySdk =
    enablePaySDKWeb && serviceAllowCard && !isCashlessPaymentDisabled;

  const location = useMemo(
    () => {
      const pickUpPoi = _get(deliveries, '[0].detail.pickUp.poi', {});
      const latlng = _get(pickUpPoi, 'latlng', {});
      return {
        latitude: _get(latlng, 'latitude', 0),
        longitude: _get(latlng, 'longitude', 0),
      };
    },
    [deliveries],
  );

  const selectedPaymentTypeID = useMemo(
    () => {
      const allPaymentTypeIDs = deliveries.map(delivery =>
        _get(delivery, 'detail.paymentTypeID'),
      );
      if (
        deliveries.length > 0 &&
        _every(
          allPaymentTypeIDs,
          paymentTypeID => paymentTypeID === allPaymentTypeIDs[0],
        )
      ) {
        const id = allPaymentTypeIDs[0];
        return id != null ? id.toString() : '';
      }
      return '';
    },
    [deliveries],
  );

  const [isPaymentDrawerOpen, setIsPaymentDrawerOpen] = useState(false);
  const [paySdkScriptReady, setPaySdkScriptReady] = useState(false);
  // True after an explicit user selection; blocks recommendation auto-fires
  // from overriding a deliberate choice.
  const hasManuallySelectedRef = useRef(false);
  // Last raw PaySDK ID — passed to PaySdkPaymentList to pre-highlight the active method.
  const [paySdkRawPaymentID, setPaySdkRawPaymentID] = useState('');

  // Derive from selectedPaymentTypeID so it also covers the auto-select case
  // where cash is picked without going through handlePaySdkPaymentSelect.
  const isCashSelected =
    selectedPaymentTypeID === CASH_BY_SENDER ||
    selectedPaymentTypeID === CASH_BY_RECIPIENT;

  const showCashPaidByDropdown =
    showPaySdk &&
    isCashSelected &&
    serviceAllowCash &&
    (isSameDayService || !isCashPaymentDisabled);

  // Dispatches a PaySDK selection to Redux and tracks the raw PaySDK ID.
  // Shared between manual list selection and recommendation auto-fires.
  const applyPaySdkSelection = useCallback(
    ({ paymentTypeId, paymentMethodType, shortTitle }) => {
      const paymentTypeID = resolvePaySdkPaymentTypeID({
        paymentID: paymentTypeId,
        paymentMethods,
        deliveries,
        city,
        isCashlessPaymentDisabled,
        isCashPaymentDisabled,
      });
      if (!paymentTypeID) {
        return null;
      }
      setPaySdkRawPaymentID(String(paymentTypeId));
      const isCash =
        paymentTypeID === CASH_BY_SENDER || paymentTypeID === CASH_BY_RECIPIENT;
      dispatch(
        NewBookingDuck.creators.bulkUpdateDeliveryDetail(deliveryIDs, {
          paymentTypeID,
          // Wallet IDs (OVO, GrabPay) are not in the Redux paymentMethods list;
          // persist metadata so the booking payload can populate type/name.
          // For cash, clear any stale wallet/card metadata from a prior selection.
          paySdkPaymentType: isCash ? '' : paymentMethodType || '',
          paySdkPaymentName: isCash ? '' : shortTitle || '',
        }),
      );
      return paymentTypeID;
    },
    [
      dispatch,
      deliveryIDs,
      paymentMethods,
      deliveries,
      city,
      isCashlessPaymentDisabled,
      isCashPaymentDisabled,
    ],
  );

  // Ignore events when the drawer is closed — those are PaySDK auto-fires
  // (resize, re-init) and should not override the user's explicit choice.
  const handlePaySdkPaymentSelect = useCallback(
    selection => {
      if (!isPaymentDrawerOpen) return;
      hasManuallySelectedRef.current = true;
      const paymentTypeID = applyPaySdkSelection(selection);
      if (!paymentTypeID) return;
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeExpressBukUploadBookPaymentMethod(
          paymentTypeID,
          payAndBookContext.totalFinalFare,
        ),
      );
    },
    [
      applyPaySdkSelection,
      isPaymentDrawerOpen,
      dispatch,
      payAndBookContext.totalFinalFare,
    ],
  );

  const handleOpenPaymentList = useCallback(
    () => setIsPaymentDrawerOpen(true),
    [],
  );

  const handlePaymentRecommended = useCallback(
    selection => {
      if (!hasManuallySelectedRef.current) {
        applyPaySdkSelection(selection);
      }
    },
    [applyPaySdkSelection],
  );

  useEffect(
    () => {
      if (!showPaySdk) return;
      loadPaySdkScript()
        .then(() => setPaySdkScriptReady(true))
        .catch(() => setPaySdkScriptReady(false));
    },
    [showPaySdk],
  );

  useEffect(
    () => {
      if (!showPaySdk) return;
      if (selectedPaymentTypeID) return;
      if (deliveries.length === 0) return;
      if (!_every(deliveries, delivery => !delivery.fetchingQuote)) return;

      const accountPaymentMethods = [...paymentMethods, ...CashPaymentMethods];
      const currency = _get(deliveries[0], 'quote.currency');
      const totalFinalFare = _sumBy(deliveries, delivery =>
        _get(delivery, 'quote.finalFare.lowerBound', 0),
      );
      const paymentMethodOptions = {
        totalAmount: calcPrice(totalFinalFare, currency),
        totalDeliveries: deliveries.length,
        isCashlessPaymentDisabled,
        isCashPaymentDisabled,
      };
      const rendered = accountPaymentMethods.map(pm =>
        generatePaymentMethodToRender(
          pm,
          deliveries[0],
          city,
          paymentMethodOptions,
        ),
      );
      // If any delivery would disable a method, reflect that across the board
      // (same semantics as `PaymentMethodSelector`).
      deliveries.slice(1).forEach(delivery => {
        accountPaymentMethods.forEach((pm, index) => {
          const overwrite = generatePaymentMethodToRender(
            pm,
            delivery,
            city,
            paymentMethodOptions,
          );
          if (!rendered[index].disabled && overwrite.disabled) {
            rendered[index].disabled = overwrite.disabled;
          }
        });
      });

      const autoSelect =
        rendered.find(pm => pm.isPrimary && !pm.disabled) ||
        rendered.find(pm => !pm.disabled);
      if (!autoSelect) return;

      dispatch(
        NewBookingDuck.creators.bulkUpdateDeliveryDetail(deliveryIDs, {
          paymentTypeID: autoSelect.paymentTypeID,
        }),
      );
    },
    [
      showPaySdk,
      selectedPaymentTypeID,
      deliveries,
      deliveryIDs,
      paymentMethods,
      city,
      isCashlessPaymentDisabled,
      isCashPaymentDisabled,
      dispatch,
    ],
  );

  return (
    <PayAndBookContext.Provider value={payAndBookContext}>
      <div className={styles.payAndBook}>
        <div className={styles.leftBlocks}>
          <div className={styles.section}>
            {refreshFailedQuotesButton}
            <h2 className={styles.sectionTitle}>
              {I18n.t(
                'phrase.geWebBulkUpload.express_web_bulk_upload_heading_payment_method_happypath',
              )}
            </h2>
            {showPaySdk ? (
              <div className={styles.paymentRecommendationWrapper}>
                <div className={styles.paymentRecommendationItem}>
                  <PaySdkPaymentRecommendation
                    onOpenPaymentList={handleOpenPaymentList}
                    onPaymentRecommended={handlePaymentRecommended}
                    selectedPaymentTypeID={selectedPaymentTypeID}
                    deliveries={deliveries}
                    location={location}
                    countryCode={countryCode}
                    enterprise={enterprise}
                  />
                </div>
                {showCashPaidByDropdown && (
                  <SelectField
                    form={form}
                    id={deliveryFormFields.paymentTypeID.fieldName}
                    fieldOptions={{
                      initialValue: selectedPaymentTypeID,
                      rules: deliveryFormFields.paymentTypeID.makeRules(),
                    }}
                    placeholder={I18n.t(
                      'v2.homepage.manualOrder.placeholder.paymentMethod',
                    )}
                    fieldClassName={styles.cashPaidByField}
                    options={[
                      {
                        value: CASH_BY_SENDER,
                        title: I18n.t('booking.paymentMethod.cashBySender'),
                        child: I18n.t('booking.paymentMethod.cashBySender'),
                      },
                      {
                        value: CASH_BY_RECIPIENT,
                        title: I18n.t('booking.paymentMethod.cashByRecipient'),
                        child: I18n.t('booking.paymentMethod.cashByRecipient'),
                        disabled: isMSD,
                      },
                    ]}
                  />
                )}
              </div>
            ) : (
              <PaymentMethodSelector deliveryIDs={deliveryIDs} />
            )}
          </div>
          {showPaySdk && (
            <Drawer
              width={495}
              placement="right"
              visible={isPaymentDrawerOpen}
              onClose={() => setIsPaymentDrawerOpen(false)}
              closable
              height="100%"
              className={styles.paymentDrawer}
            >
              <div className={styles.paymentDrawerBody}>
                {paySdkScriptReady ? (
                  <PaySdkPaymentList
                    onSelectPayment={handlePaySdkPaymentSelect}
                    selectedPaymentTypeID={paySdkRawPaymentID}
                    deliveries={deliveries}
                    location={location}
                    countryCode={countryCode}
                    enterprise={enterprise}
                  />
                ) : (
                  <div className={styles.paymentDrawerLoading}>
                    <Spin
                      tip={I18n.t(
                        'v2.homepage.manualOrder.loadingPaymentOptions',
                      )}
                    />
                  </div>
                )}
              </div>
            </Drawer>
          )}
          {showEnterpriseTripInput && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>
                {I18n.t('v2.homepage.manualOrder.heading.paymentDetails')}
              </h2>
              <div className={styles.tripInputWrapper}>
                <InputField
                  id={deliveryFormFields.tripCode.fieldName}
                  fieldClassName={styles.tripInput}
                  placeholder={I18n.t(
                    'v2.homepage.manualOrder.placeholder.tripCode',
                  )}
                  fieldOptions={{
                    initialValue: tripCode,
                    rules: deliveryFormFields.tripCode.makeRules(),
                  }}
                  form={form}
                />
                <InputField
                  id={deliveryFormFields.tripDescription.fieldName}
                  fieldClassName={styles.tripInput}
                  placeholder={I18n.t(
                    'v2.homepage.manualOrder.placeholder.tripDescription',
                  )}
                  fieldOptions={{
                    initialValue: tripDescription,
                    rules: deliveryFormFields.tripDescription.makeRules(),
                  }}
                  form={form}
                />
              </div>
            </div>
          )}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {I18n.t(
                'phrase.geWebBulkUpload.express_web_bulk_upload_heading_offers_happypath',
              )}
            </h2>
            <OfferSelector deliveryIDs={deliveryIDs} />
          </div>
        </div>
        <div className={styles.rightBlocks}>
          <PriceSection deliveryIDs={deliveryIDs} />
        </div>
      </div>
    </PayAndBookContext.Provider>
  );
};

PayAndBook.defaultProps = {
  offerConfig: null,
  showEnterpriseTripInput: false,
  enablePaySDKWeb: false,
  enterprise: {},
  isCashlessPaymentDisabled: false,
  isCashPaymentDisabled: false,
  paymentMethods: [],
  city: {},
  countryCode: '',
};

PayAndBook.propTypes = {
  deliveryIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  deliveries: PropTypes.arrayOf(NewBookingDeliveryPropType).isRequired,
  dispatch: PropTypes.func.isRequired,
  offerConfig: PropTypes.shape({}),
  form: PropTypes.shape({}).isRequired,
  showEnterpriseTripInput: PropTypes.bool,
  enablePaySDKWeb: PropTypes.bool,
  enterprise: PropTypes.shape({
    groupID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    profileType: PropTypes.string,
  }),
  isCashlessPaymentDisabled: PropTypes.bool,
  isCashPaymentDisabled: PropTypes.bool,
  paymentMethods: PropTypes.arrayOf(PropTypes.shape({})),
  city: CityPropType,
  countryCode: PropTypes.string,
};

const onFieldsChange = ({ dispatch, deliveryIDs }, changedFields, _) => {
  Object.entries(changedFields).forEach(([fn, fv]) => {
    dispatch(
      NewBookingDuck.creators.bulkUpdateDeliveryDetail(deliveryIDs, {
        [fn]: fv.value,
      }),
    );
  });
};

export default connect(state => {
  const city = selectedCitySelector(state);
  return {
    deliveryIDs: bulkUploadDeliveryIDsSelector(state),
    deliveries: bulkUploadDeliveriesSelector(state),
    offerConfig: bulkUploadOfferConfigSelector(state),
    showEnterpriseTripInput: showEnterpriseTripInputSelector(state),
    enablePaySDKWeb: enablePaySDKWebSelector(state),
    enterprise: enterpriseForPaySdkSelector(state),
    isCashlessPaymentDisabled: isCashlessPaymentDisabledSelector(state),
    isCashPaymentDisabled: isCashPaymentDisabledSelector(state),
    paymentMethods: paymentMethodSelector(state),
    city,
    countryCode:
      _get(city, 'countryCode') || getUserCountryCodeFromState(state) || '',
  };
})(Form.create({ onFieldsChange })(PayAndBook));
